// src/pages/SaccoFinancialStatePage.jsx
// ✅ SACCO Financial State - Button Navigation with Historical Table Main View
// Assets = Opening Balance + Shares + Interest - Outstanding Loans + Fees - Expenses

import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const SaccoFinancialStatePage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [activeSection, setActiveSection] = useState(null); // null = show historical table only
  const [expandedHistoryMonth, setExpandedHistoryMonth] = useState(null); // monthKey of expanded row
  const [openingBalance, setOpeningBalance] = useState('');
  const [savingBalance, setSavingBalance] = useState(false);
  const [bankStatementBalance, setBankStatementBalance] = useState('');
  const [bankStatementAsOfDate, setBankStatementAsOfDate] = useState('');
  const [savingBankStatement, setSavingBankStatement] = useState(false);

  useEffect(() => {
    fetchFinancialState();
    
    // Add CSS to fix table header hover
    const style = document.createElement('style');
    style.textContent = `
      .data-table thead th {
        color: var(--gray-900) !important;
        background: var(--gray-100) !important;
      }
      .data-table thead th:hover {
        color: var(--gray-900) !important;
        background: var(--gray-200) !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const fetchFinancialState = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('hopeSaccoToken');
      const response = await fetch(`${API_URL}/sacco-financial-state`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const result = await response.json();
      
      if (result.success) {
        setData(result.data);
        setOpeningBalance(result.data.summary.openingBalance.toString());
        setBankStatementBalance(result.data.summary.bankStatementBalance.toString());
        setBankStatementAsOfDate(result.data.summary.bankStatementAsOfDate || '');
      } else {
        setError(result.message || 'Failed to load financial state');
      }
    } catch (err) {
      console.error('Error fetching financial state:', err);
      setError('Failed to load financial state');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveOpeningBalance = async () => {
    setSavingBalance(true);
    try {
      const token = localStorage.getItem('hopeSaccoToken');
      const response = await fetch(`${API_URL}/sacco-financial-state/opening-balance`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ amount: parseFloat(openingBalance) || 0 })
      });
      const result = await response.json();
      
      if (result.success) {
        alert('Opening balance updated successfully!');
        fetchFinancialState();
      } else {
        alert('Failed to update: ' + result.message);
      }
    } catch (err) {
      console.error('Error saving opening balance:', err);
      alert('Failed to save opening balance');
    } finally {
      setSavingBalance(false);
    }
  };

  const handleSaveBankStatementBalance = async () => {
    setSavingBankStatement(true);
    try {
      const token = localStorage.getItem('hopeSaccoToken');
      const response = await fetch(`${API_URL}/sacco-financial-state/bank-statement-balance`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount: parseFloat(bankStatementBalance) || 0,
          asOfDate: bankStatementAsOfDate || '',
        })
      });
      const result = await response.json();
      
      if (result.success) {
        alert('Bank statement balance updated successfully!');
        fetchFinancialState();
      } else {
        alert('Failed to update: ' + result.message);
      }
    } catch (err) {
      console.error('Error saving bank statement balance:', err);
      alert('Failed to save bank statement balance');
    } finally {
      setSavingBankStatement(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-KE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="loading" style={{ textAlign: 'center', padding: '40px' }}>
        Loading financial state...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '15px' }}>
        <div className="alert alert-error">{error}</div>
        <button onClick={fetchFinancialState} className="btn btn-primary" style={{ marginTop: '10px' }}>
          Retry
        </button>
      </div>
    );
  }

  if (!data) return null;

  const { summary, shares, loansAllTime, loansByType, fees, currentMonth, assetsHistory } = data;

  return (
    <div style={{ padding: '15px' }}>
      {/* HEADER */}
      <div style={{ textAlign: 'center', marginBottom: '20px' }}>
        <h2 style={{ fontSize: '24px', marginBottom: '8px' }}>📊 SACCO Financial State</h2>
        <p style={{ color: 'var(--gray-500)', fontSize: '12px', marginBottom: '10px' }}>
          As of {formatDate(summary.asOfDate)}
        </p>
        <button onClick={fetchFinancialState} className="btn btn-outline btn-sm">
          🔄 Refresh
        </button>
      </div>

      {/* OPENING BALANCE INPUT */}
      <div className="card" style={{ padding: '15px', marginBottom: '20px', background: 'var(--gray-100)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <label style={{ fontWeight: '600', fontSize: '14px', minWidth: '150px' }}>
            Opening Balance (Jan 2025):
          </label>
          <input
            type="number"
            value={openingBalance}
            onChange={(e) => setOpeningBalance(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid var(--gray-300)',
              borderRadius: '6px',
              fontSize: '14px',
              width: '200px'
            }}
            placeholder="Enter amount"
          />
          <button 
            onClick={handleSaveOpeningBalance}
            disabled={savingBalance}
            className="btn btn-primary btn-sm"
          >
            {savingBalance ? 'Saving...' : 'Save'}
          </button>
          <span style={{ fontSize: '12px', color: 'var(--gray-500)' }}>
            Current: {formatCurrency(summary.openingBalance)}
          </span>
        </div>
      </div>

      {/* CURRENT ASSETS SUMMARY */}
      <div className="card" style={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
        color: 'white',
        padding: '20px',
        textAlign: 'center',
        marginBottom: '20px'
      }}>
        <h3 style={{ fontSize: '16px', marginBottom: '10px', opacity: 0.9 }}>Current SACCO Assets (Bank Balance)</h3>
        <div style={{ fontSize: '36px', fontWeight: '800', marginBottom: '10px' }}>
          {formatCurrency(summary.totalAssets)}
        </div>
        <p style={{ fontSize: '11px', opacity: 0.9, margin: 0 }}>
          Opening: {formatCurrency(summary.openingBalance)} + 
          Shares: {formatCurrency(summary.totalSharesContributed)} + 
          Interest: {formatCurrency(summary.totalInterestEarned)} - 
          Outstanding Loans: {formatCurrency(summary.totalLoansOutstanding)} + 
          Fees: {formatCurrency(summary.totalApplicationFees)} - 
          Expenses: {formatCurrency(summary.totalExpenses)}
        </p>
      </div>

      {/* BANK STATEMENT RECONCILIATION */}
      <div className="card" style={{ padding: '15px', marginBottom: '20px', background: 'var(--gray-100)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap', marginBottom: '12px' }}>
          <label style={{ fontWeight: '600', fontSize: '14px', minWidth: '150px' }}>
            Bank Statement Says:
          </label>
          <input
            type="number"
            value={bankStatementBalance}
            onChange={(e) => setBankStatementBalance(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid var(--gray-300)',
              borderRadius: '6px',
              fontSize: '14px',
              width: '200px'
            }}
            placeholder="Enter amount from statement"
          />
          <input
            type="date"
            value={bankStatementAsOfDate}
            onChange={(e) => setBankStatementAsOfDate(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid var(--gray-300)',
              borderRadius: '6px',
              fontSize: '14px',
            }}
            title="Date the bank statement is dated as at"
          />
          <button 
            onClick={handleSaveBankStatementBalance}
            disabled={savingBankStatement}
            className="btn btn-primary btn-sm"
          >
            {savingBankStatement ? 'Saving...' : 'Save'}
          </button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap', fontSize: '13px' }}>
          <span>System shows: <strong>{formatCurrency(summary.totalAssets)}</strong></span>
          <span>Bank says: <strong>{formatCurrency(summary.bankStatementBalance)}</strong>{summary.bankStatementAsOfDate ? ` (as at ${summary.bankStatementAsOfDate})` : ''}</span>
          <span style={{ 
            fontWeight: '700',
            color: Math.abs(summary.reconciliationGap) < 1 ? 'var(--success-main)' : 'var(--error-main)'
          }}>
            Difference: {formatCurrency(summary.reconciliationGap)}
          </span>
        </div>
        {summary.bankStatementBalance === 0 && (
          <p style={{ fontSize: '11px', color: 'var(--gray-500)', marginTop: '8px', marginBottom: 0, fontStyle: 'italic' }}>
            * Enter the actual balance from your latest bank statement above to see the reconciliation gap.
          </p>
        )}
      </div>

      {/* HISTORICAL ASSETS TABLE - MAIN DEFAULT VIEW */}
      <div style={{ marginBottom: '25px' }}>
        <h3 style={{ 
          fontSize: '18px', 
          marginBottom: '12px', 
          borderBottom: '3px solid var(--primary-main)', 
          paddingBottom: '8px',
          fontWeight: '700'
        }}>
          📅 Historical Assets (Month by Month) - MAIN DASHBOARD
        </h3>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Month</th>
                <th style={{ textAlign: 'right' }}>Shares Contributed</th>
                <th style={{ textAlign: 'right' }}>Normal Interest</th>
                <th style={{ textAlign: 'right' }}>Emergency Interest</th>
                <th style={{ textAlign: 'right' }}>School Interest</th>
                <th style={{ textAlign: 'right' }}>Application Fees</th>
                <th style={{ textAlign: 'right' }}>Net Income</th>
                <th style={{ textAlign: 'right' }}>Total Assets</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {assetsHistory && assetsHistory.length > 0 ? (
                assetsHistory.map((record, index) => {
                  const isExpanded = expandedHistoryMonth === record.monthKey;
                  return (
                    <React.Fragment key={index}>
                      <tr
                        onClick={() => setExpandedHistoryMonth(isExpanded ? null : record.monthKey)}
                        style={{ cursor: 'pointer', background: isExpanded ? 'var(--gray-100)' : undefined }}
                      >
                        <td>
                          <strong>{record.monthLabel}</strong>
                          {record.flowsSource === 'committed' ? (
                            <span title="Real saved 'Save Month' records" style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, color: 'var(--success-main)', border: '1px solid var(--success-main)', borderRadius: 4, padding: '1px 5px' }}>
                              ✓ COMMITTED
                            </span>
                          ) : (
                            <span title="No committed records yet for this month — figures are simulated from the loan schedule" style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, color: 'var(--gray-500)', border: '1px solid var(--gray-300, #ccc)', borderRadius: 4, padding: '1px 5px' }}>
                              ~ SIMULATED
                            </span>
                          )}
                        </td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(record.sharesContribution)}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(record.normalInterest)}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(record.emergencyInterest)}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(record.schoolInterest)}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(record.applicationFees)}</td>
                        <td style={{ textAlign: 'right', color: record.netIncome >= 0 ? 'var(--success-main)' : 'var(--error-main)' }}>
                          <strong>{formatCurrency(record.netIncome)}</strong>
                        </td>
                        <td style={{ textAlign: 'right' }}><strong>{formatCurrency(record.totalAssets)}</strong></td>
                        <td style={{ fontSize: '11px', color: 'var(--gray-500)', whiteSpace: 'nowrap' }}>
                          {isExpanded ? '▲ collapse' : '▼ audit detail'}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={9} style={{ padding: 0, borderBottom: '2px solid var(--gray-200)' }}>
                            <div style={{ background: 'var(--gray-50, #f8f8ff)', padding: '16px 20px', borderLeft: '4px solid var(--primary-main)' }}>
                              <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--gray-600)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
                                Monthly Audit Trail — {record.monthLabel}
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px 24px', fontSize: 13, marginBottom: 12 }}>
                                {[
                                  ['Normal Loan Repayment', formatCurrency(record.normalRepayment)],
                                  ['Normal Loan Outstanding', formatCurrency(record.normalOutstanding)],
                                  ['School Loan Repayment', formatCurrency(record.schoolRepayment)],
                                  ['School Loan Outstanding', formatCurrency(record.schoolOutstanding)],
                                  ['Emergency Loan Repayment', formatCurrency(record.emergencyRepayment)],
                                  ['Emergency Loan Outstanding', formatCurrency(record.emergencyOutstanding)],
                                  ['Expenses This Month', formatCurrency(record.totalExpenses)],
                                  ['Loans Disbursed This Month', formatCurrency(record.totalLoansTaken)],
                                  ['Available for Lending', formatCurrency(record.remainingBalance)],
                                ].map(([label, value]) => (
                                  <div key={label}>
                                    <span style={{ color: 'var(--gray-500)', display: 'block', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
                                    <span style={{ fontWeight: 600 }}>{value}</span>
                                  </div>
                                ))}
                              </div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, paddingTop: 10, borderTop: '1px solid var(--gray-200)', fontSize: 13 }}>
                                <span>Total Interest: <strong>{formatCurrency(record.totalInterest)}</strong></span>
                                <span>Total Repayment: <strong>{formatCurrency(record.totalRepayment)}</strong></span>
                                <span>Total Collected This Month: <strong>{formatCurrency(record.totalCollected)}</strong></span>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="9" style={{ textAlign: 'center', padding: '20px', color: 'var(--gray-500)' }}>
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <p style={{ fontSize: '11px', color: 'var(--gray-500)', marginTop: '8px', fontStyle: 'italic' }}>
          * Net Income = Interest + Application Fees − Expenses (what the SACCO actually made that month). Shares Contributed is NOT income — it's member capital the SACCO owes back, shown for the audit trail only. Total Assets is the cumulative running balance since January 2025, not a sum of this row's other columns. Click any row for repayments, outstanding balances, and more. Closed months are computed once and saved permanently in Firebase — they won't change even if you later edit a loan; only the current (still-open) month is recalculated live.
        </p>
      </div>

      {/* NAVIGATION BUTTONS */}
      <div style={{ marginBottom: '20px', borderTop: '2px solid var(--gray-200)', paddingTop: '20px' }}>
        <h4 style={{ fontSize: '14px', marginBottom: '12px', color: 'var(--gray-600)' }}>
          📑 View Detailed Reports (Click button to view):
        </h4>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
          <button 
            className={`btn ${activeSection === 'shares' ? 'btn-primary' : 'btn-outline'} btn-sm`}
            onClick={() => setActiveSection(activeSection === 'shares' ? null : 'shares')}
          >
            💰 Member Shares
          </button>
          <button 
            className={`btn ${activeSection === 'loans-overview' ? 'btn-primary' : 'btn-outline'} btn-sm`}
            onClick={() => setActiveSection(activeSection === 'loans-overview' ? null : 'loans-overview')}
          >
            💳 Loans Overview
          </button>
          <button 
            className={`btn ${activeSection === 'normal' ? 'btn-primary' : 'btn-outline'} btn-sm`}
            onClick={() => setActiveSection(activeSection === 'normal' ? null : 'normal')}
          >
            🏦 Normal Loans
          </button>
          <button 
            className={`btn ${activeSection === 'school' ? 'btn-primary' : 'btn-outline'} btn-sm`}
            onClick={() => setActiveSection(activeSection === 'school' ? null : 'school')}
          >
            🎓 School Fees
          </button>
          <button 
            className={`btn ${activeSection === 'emergency' ? 'btn-primary' : 'btn-outline'} btn-sm`}
            onClick={() => setActiveSection(activeSection === 'emergency' ? null : 'emergency')}
          >
            🚨 Emergency
          </button>
          <button 
            className={`btn ${activeSection === 'fees' ? 'btn-primary' : 'btn-outline'} btn-sm`}
            onClick={() => setActiveSection(activeSection === 'fees' ? null : 'fees')}
          >
            📝 Fees
          </button>
          <button 
            className={`btn ${activeSection === 'expenses' ? 'btn-primary' : 'btn-outline'} btn-sm`}
            onClick={() => setActiveSection(activeSection === 'expenses' ? null : 'expenses')}
          >
            💸 Expenses
          </button>
          <button 
            className={`btn ${activeSection === 'current-month' ? 'btn-primary' : 'btn-outline'} btn-sm`}
            onClick={() => setActiveSection(activeSection === 'current-month' ? null : 'current-month')}
          >
            📅 Current Month
          </button>
        </div>
      </div>

      {/* CONDITIONAL TABLE RENDERING BASED ON ACTIVE SECTION */}
      {activeSection && (
        <div style={{ marginTop: '20px' }}>
          {/* MEMBER SHARES TABLE */}
          {activeSection === 'shares' && (
            <div>
              <h3 style={{ fontSize: '18px', marginBottom: '12px', borderBottom: '3px solid var(--primary-main)', paddingBottom: '8px', fontWeight: '700' }}>
                💰 Member Shares
              </h3>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th style={{ textAlign: 'right' }}>Amount</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><strong>Total Members</strong></td>
                      <td style={{ textAlign: 'right' }}><strong>{summary.totalMembers}</strong></td>
                      <td>Active members</td>
                    </tr>
                    <tr>
                      <td><strong>Total Shares</strong></td>
                      <td style={{ textAlign: 'right' }}><strong>{formatCurrency(shares.totalShares)}</strong></td>
                      <td>All member contributions</td>
                    </tr>
                    <tr>
                      <td><strong>Share Capital (Locked)</strong></td>
                      <td style={{ textAlign: 'right' }}><strong>{formatCurrency(shares.shareCapital)}</strong></td>
                      <td>KES 5,000 × {summary.totalMembers} members</td>
                    </tr>
                    <tr>
                      <td><strong>Available Shares</strong></td>
                      <td style={{ textAlign: 'right' }}><strong>{formatCurrency(shares.availableShares)}</strong></td>
                      <td>Withdrawable amount</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* LOANS OVERVIEW TABLE */}
          {activeSection === 'loans-overview' && (
            <div>
              <h3 style={{ fontSize: '18px', marginBottom: '12px', borderBottom: '3px solid var(--primary-main)', paddingBottom: '8px', fontWeight: '700' }}>
                💳 Loans Overview (All Time)
              </h3>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Metric</th>
                      <th style={{ textAlign: 'right' }}>Amount</th>
                      <th>Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><strong>Total Disbursed</strong></td>
                      <td style={{ textAlign: 'right' }}><strong>{formatCurrency(loansAllTime.totalDisbursed)}</strong></td>
                      <td>{loansAllTime.totalCount} loans</td>
                    </tr>
                    <tr style={{ background: 'var(--success-main)15' }}>
                      <td><strong>Total Repaid</strong></td>
                      <td style={{ textAlign: 'right' }}><strong>{formatCurrency(loansAllTime.totalPaid)}</strong></td>
                      <td>{((loansAllTime.totalPaid / loansAllTime.totalDisbursed) * 100).toFixed(1)}% recovered</td>
                    </tr>
                    <tr style={{ background: 'var(--warning-main)15' }}>
                      <td><strong>Total Outstanding</strong></td>
                      <td style={{ textAlign: 'right' }}><strong>{formatCurrency(loansAllTime.totalOutstanding)}</strong></td>
                      <td>{loansAllTime.activeCount} active loans</td>
                    </tr>
                    <tr>
                      <td><strong>Completed Loans</strong></td>
                      <td style={{ textAlign: 'right' }}><strong>{loansAllTime.completedCount}</strong></td>
                      <td>Fully repaid</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* NORMAL LOANS TABLE */}
          {activeSection === 'normal' && (
            <div>
              <h3 style={{ fontSize: '18px', marginBottom: '12px', borderBottom: '3px solid var(--primary-main)', paddingBottom: '8px', fontWeight: '700' }}>
                🏦 Normal Loans
              </h3>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Metric</th>
                      <th style={{ textAlign: 'right' }}>Amount</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><strong>Disbursed</strong></td>
                      <td style={{ textAlign: 'right' }}><strong>{formatCurrency(loansByType.normal.disbursed)}</strong></td>
                      <td>{loansByType.normal.count} loans</td>
                    </tr>
                    <tr style={{ background: 'var(--success-main)15' }}>
                      <td><strong>Repaid</strong></td>
                      <td style={{ textAlign: 'right' }}><strong>{formatCurrency(loansByType.normal.paid)}</strong></td>
                      <td>{loansByType.normal.count > 0 ? ((loansByType.normal.paid / loansByType.normal.disbursed) * 100).toFixed(1) : 0}%</td>
                    </tr>
                    <tr style={{ background: 'var(--warning-main)15' }}>
                      <td><strong>Outstanding</strong></td>
                      <td style={{ textAlign: 'right' }}><strong>{formatCurrency(loansByType.normal.outstanding)}</strong></td>
                      <td>{loansByType.normal.activeCount} active</td>
                    </tr>
                    <tr style={{ background: 'var(--primary-light)15' }}>
                      <td><strong>Current Month Interest</strong></td>
                      <td style={{ textAlign: 'right' }}><strong>{formatCurrency(loansByType.normal.interest)}</strong></td>
                      <td>1% of outstanding</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* SCHOOL FEES LOANS TABLE */}
          {activeSection === 'school' && (
            <div>
              <h3 style={{ fontSize: '18px', marginBottom: '12px', borderBottom: '3px solid var(--primary-main)', paddingBottom: '8px', fontWeight: '700' }}>
                🎓 School Fees Loans
              </h3>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Metric</th>
                      <th style={{ textAlign: 'right' }}>Amount</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><strong>Disbursed</strong></td>
                      <td style={{ textAlign: 'right' }}><strong>{formatCurrency(loansByType.school.disbursed)}</strong></td>
                      <td>{loansByType.school.count} loans</td>
                    </tr>
                    <tr style={{ background: 'var(--success-main)15' }}>
                      <td><strong>Repaid</strong></td>
                      <td style={{ textAlign: 'right' }}><strong>{formatCurrency(loansByType.school.paid)}</strong></td>
                      <td>{loansByType.school.count > 0 ? ((loansByType.school.paid / loansByType.school.disbursed) * 100).toFixed(1) : 0}%</td>
                    </tr>
                    <tr style={{ background: 'var(--warning-main)15' }}>
                      <td><strong>Outstanding</strong></td>
                      <td style={{ textAlign: 'right' }}><strong>{formatCurrency(loansByType.school.outstanding)}</strong></td>
                      <td>{loansByType.school.activeCount} active</td>
                    </tr>
                    <tr style={{ background: 'var(--primary-light)15' }}>
                      <td><strong>Current Month Interest</strong></td>
                      <td style={{ textAlign: 'right' }}><strong>{formatCurrency(loansByType.school.interest)}</strong></td>
                      <td>1% of outstanding</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* EMERGENCY LOANS TABLE */}
          {activeSection === 'emergency' && (
            <div>
              <h3 style={{ fontSize: '18px', marginBottom: '12px', borderBottom: '3px solid var(--primary-main)', paddingBottom: '8px', fontWeight: '700' }}>
                🚨 Emergency Loans
              </h3>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Metric</th>
                      <th style={{ textAlign: 'right' }}>Amount</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><strong>Disbursed</strong></td>
                      <td style={{ textAlign: 'right' }}><strong>{formatCurrency(loansByType.emergency.disbursed)}</strong></td>
                      <td>{loansByType.emergency.count} loans</td>
                    </tr>
                    <tr style={{ background: 'var(--success-main)15' }}>
                      <td><strong>Repaid</strong></td>
                      <td style={{ textAlign: 'right' }}><strong>{formatCurrency(loansByType.emergency.paid)}</strong></td>
                      <td>{loansByType.emergency.count > 0 ? ((loansByType.emergency.paid / loansByType.emergency.disbursed) * 100).toFixed(1) : 0}%</td>
                    </tr>
                    <tr style={{ background: 'var(--warning-main)15' }}>
                      <td><strong>Outstanding</strong></td>
                      <td style={{ textAlign: 'right' }}><strong>{formatCurrency(loansByType.emergency.outstanding)}</strong></td>
                      <td>{loansByType.emergency.activeCount} active</td>
                    </tr>
                    <tr style={{ background: 'var(--primary-light)15' }}>
                      <td><strong>Current Month Interest</strong></td>
                      <td style={{ textAlign: 'right' }}><strong>{formatCurrency(loansByType.emergency.interest)}</strong></td>
                      <td>1% of outstanding</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* FEES TABLE */}
          {activeSection === 'fees' && (
            <div>
              <h3 style={{ fontSize: '18px', marginBottom: '12px', borderBottom: '3px solid var(--primary-main)', paddingBottom: '8px', fontWeight: '700' }}>
                📝 Application Fees
              </h3>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th style={{ textAlign: 'right' }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><strong>Total Fees Collected</strong></td>
                      <td style={{ textAlign: 'right' }}><strong>{formatCurrency(fees.totalApplicationFees)}</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* EXPENSES TABLE */}
          {activeSection === 'expenses' && (
            <div>
              <h3 style={{ fontSize: '18px', marginBottom: '12px', borderBottom: '3px solid var(--primary-main)', paddingBottom: '8px', fontWeight: '700' }}>
                💸 Expenses
              </h3>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th style={{ textAlign: 'right' }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><strong>Total Expenses (All Time)</strong></td>
                      <td style={{ textAlign: 'right' }}><strong>{formatCurrency(summary.totalExpenses)}</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p style={{ fontSize: '11px', color: 'var(--gray-500)', marginTop: '8px', fontStyle: 'italic' }}>
                * See the Expenses tab under Reports for a full itemized breakdown by category, date, and who recorded each entry.
              </p>
            </div>
          )}

          {/* CURRENT MONTH TABLE */}
          {activeSection === 'current-month' && (
            <div>
              <h3 style={{ fontSize: '18px', marginBottom: '12px', borderBottom: '3px solid var(--primary-main)', paddingBottom: '8px', fontWeight: '700' }}>
                📅 Current Month Projections
              </h3>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th style={{ textAlign: 'right' }}>Amount</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><strong>Interest</strong></td>
                      <td style={{ textAlign: 'right' }}><strong>{formatCurrency(currentMonth.expectedInterest)}</strong></td>
                      <td>1% of balance</td>
                    </tr>
                    <tr>
                      <td><strong>Principal</strong></td>
                      <td style={{ textAlign: 'right' }}><strong>{formatCurrency(currentMonth.expectedPrincipalRepayments)}</strong></td>
                      <td>Scheduled payments</td>
                    </tr>
                    <tr style={{ background: 'var(--primary-light)15' }}>
                      <td><strong>Total</strong></td>
                      <td style={{ textAlign: 'right' }}><strong>{formatCurrency(currentMonth.expectedTotalRepayments)}</strong></td>
                      <td>Interest + Principal</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SaccoFinancialStatePage;
